import { api } from './api'

const STORAGE_KEY = 'toty:phone-sync-pending'
const MAX_ATTEMPTS = 5
// O previousIdToken e validado no backend com `jwt.verify` sem
// `ignoreExpiration`, entao ele morre junto com o TTL do ID token do Cognito
// (~1h). Reenviar depois disso e chamada garantidamente perdida.
const MAX_AGE_MS = 60 * 60 * 1000

const STATUS_TRANSITORIOS = [401, 408, 429]

export type PendingPhoneSync = {
  previousIdToken: string
  sub: string
  attempts: number
  createdAt: number
}

export type FlushResult = 'done' | 'retry' | 'gave-up' | 'none'

/**
 * Um erro e definitivo quando o backend respondeu e recusou de forma que
 * repetir nao muda: 409 (conflito), 403 (token invalido, telefone nao
 * verificado, sub diferente), 400 (nada a sincronizar), 404 (cadastro nao
 * encontrado).
 *
 * 401, 408 e 429 sao 4xx mas transitorios. O 401 tem caminho concreto: o
 * orulTokenManager devolve null perto da expiracao e a requisicao sai sem
 * Authorization.
 */
export function isTerminalSyncError(error: any): boolean {
  const status = error?.response?.status

  if (typeof status !== 'number') return false
  if (STATUS_TRANSITORIOS.includes(status)) return false

  return status >= 400 && status < 500
}

/**
 * Guarda a sincronizacao que falhou para reenviar depois.
 *
 * O `confirm` da troca e irreversivel: quando ele retorna, o telefone de login
 * ja mudou no Cognito. Se o `phone-sync` falhar em seguida, o cadastro do
 * cliente no toty continua apontando para o numero antigo. A pendencia existe
 * para fechar essa janela sem intervencao humana.
 *
 * O `sub` fica gravado junto porque o aparelho pode ser compartilhado (balcao):
 * sem ele, o proximo usuario a logar dispararia o reenvio com o token de outra
 * pessoa e o 403 resultante apagaria uma pendencia que nao era dele.
 */
export function savePendingSync(previousIdToken: string, sub: string): void {
  try {
    const pending: PendingPhoneSync = {
      previousIdToken,
      sub,
      attempts: 0,
      createdAt: Date.now(),
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pending))
  } catch {
    // localStorage indisponivel (modo privado, cota). A troca de login ja
    // aconteceu; nao ha o que fazer alem de seguir.
  }
}

export function readPendingSync(): PendingPhoneSync | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw)
    if (
      typeof parsed?.previousIdToken !== 'string' ||
      typeof parsed?.sub !== 'string' ||
      typeof parsed?.attempts !== 'number' ||
      typeof parsed?.createdAt !== 'number'
    ) {
      return null
    }

    return parsed as PendingPhoneSync
  } catch {
    return null
  }
}

export function clearPendingSync(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // idem savePendingSync
  }
}

function bumpAttempts(pending: PendingPhoneSync): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...pending, attempts: pending.attempts + 1 }),
    )
  } catch {
    // idem savePendingSync
  }
}

/**
 * Reenvia a sincronizacao pendente do usuario atual, se houver.
 *
 * Pendencia de outro `sub` e ignorada sem ser apagada: ela continua valendo
 * para o dono, que pode voltar a logar no mesmo aparelho dentro da janela de
 * uma hora.
 */
export async function flushPendingSync(subAtual: string): Promise<FlushResult> {
  const pending = readPendingSync()
  if (!pending) return 'none'
  if (pending.sub !== subAtual) return 'none'

  const expirou = Date.now() - pending.createdAt > MAX_AGE_MS
  if (pending.attempts >= MAX_ATTEMPTS || expirou) {
    clearPendingSync()
    return 'gave-up'
  }

  try {
    await api.post('/clients/me/phone-sync', {
      previousIdToken: pending.previousIdToken,
    })
    clearPendingSync()
    return 'done'
  } catch (error: any) {
    if (isTerminalSyncError(error)) {
      clearPendingSync()
      return 'gave-up'
    }

    bumpAttempts(pending)
    return 'retry'
  }
}
